import type { Event } from '@/api/schemas'
import { formatLocalTime, groupByLocalHour } from '@/lib/format'
import { t } from '@/lib/i18n'
import { cn } from '@/lib/utils'

interface Props {
  events: Event[]
  onSelectEvent?: (eventId: number) => void
}

export function EventsByHour({ events, onSelectEvent }: Props) {
  const groups = groupByLocalHour(events, (e) => e.timestamp)

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          {t("Today's captures")}
        </h2>
      </div>
      <div className="bg-card rounded-xl border border-border divide-y divide-border-subtle">
        {groups.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-subtle-foreground">{t('No captures yet')}</div>
        ) : (
          groups.map((group) => (
            <details key={group.hour} className="group">
              <summary className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-background list-none">
                <span className="text-xs text-subtle-foreground group-open:text-foreground-muted transition-colors">
                  ▸
                </span>
                <span className="text-xs font-medium text-foreground-soft tabular-nums">{group.hour}</span>
                <span className="text-xs text-subtle-foreground">
                  {group.items.length === 1 ? t('1 capture') : t('{n} captures', { n: group.items.length })}
                </span>
                {group.items.some((e) => e.alert) ? (
                  <span className="w-1.5 h-1.5 rounded-full bg-critical flex-shrink-0" />
                ) : null}
              </summary>
              <div className="divide-y divide-background">
                {group.items.map((event) => (
                  <EventRow key={event.id} event={event} onSelect={onSelectEvent} />
                ))}
              </div>
            </details>
          ))
        )}
      </div>
    </div>
  )
}

function EventRow({ event, onSelect }: { event: Event; onSelect?: (id: number) => void }) {
  const borderClass = event.alert
    ? event.alert.level === 'critical'
      ? 'border-l-2 border-l-critical'
      : 'border-l-2 border-l-attention'
    : ''

  const className = cn(
    'flex items-center gap-3 px-4 pl-10 py-2 hover:bg-background transition-colors w-full text-left',
    borderClass,
  )
  const content = (
    <>
      <span className="text-xs text-subtle-foreground flex-shrink-0 tabular-nums">
        {formatLocalTime(event.timestamp)}
      </span>
      <span className="text-xs text-foreground-muted flex-1 truncate">{event.camera_id}</span>
      <code className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0 font-mono">
        {event.trigger}
      </code>
    </>
  )

  if (onSelect) {
    return (
      <button
        type="button"
        className={className}
        data-event-id={event.id}
        onClick={() => onSelect(event.id)}
      >
        {content}
      </button>
    )
  }
  return (
    <div className={className} data-event-id={event.id}>
      {content}
    </div>
  )
}
